import React, { useMemo, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import SeverityBadge from '@/components/apex/SeverityBadge';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceLine,
} from 'recharts';
import { Button } from '@/components/ui/button';
import {
  Download, Share2, ArrowRight, ShieldCheck, FileSignature, Building2,
  Scale, Briefcase, TrendingDown, TrendingUp, AlertTriangle, CheckCircle2,
  PenLine, GanttChart, FileText, Copy,
} from 'lucide-react';
import ShareSnapshotDialog from '@/components/apex/ShareSnapshotDialog';
import InvestigationBadge from '@/components/apex/InvestigationBadge';
import RecordDrawer from '@/components/apex/RecordDrawer';
import { toast } from 'sonner';

// ─── Static board-quality content (presentation only) ──────────────────────
const QUARTER = 'Q2 FY2026';
const AS_OF = 'May 7, 2026 · 18:00 UTC';
const PREPARED_BY = { name: 'Marcus Hellman', role: 'Chief Information Security Officer · Stratum Financial Group' };
const PEER_REVIEWER = { name: 'Mike Hsu', role: 'Director, Internal Audit' };
const AUDITOR = 'Deloitte & Touche LLP';
const INSURER = 'Travelers Bond & Specialty';
const REGULATORS = ['NYDFS Part 500', 'OCC Heightened Standards', 'FFIEC IT Examination'];

const BOARD_NARRATIVE = `Stratum Financial Group's cloud governance posture improved materially in Q2 2026. Governance debt decreased 23% quarter-over-quarter to $847,000, which is 40% below the industry benchmark for organizations of comparable size and compliance scope.

Three active control gaps were identified and are under active remediation, down from six at the start of the quarter. The most significant finding — a dev-origin container image running in the production payments environment — was detected, investigated, and a remediation plan assigned. The environment integrity contamination score improved from 33 to 23 out of 100.

Framework coverage stands at SOC 2: 94%, ISO 27001: 91%, DORA: 88%, and PCI DSS: 96%. Seven unauthorized changes were detected in the last 30 days, all identified and documented with evidence packets. Identity right-sizing opportunities were identified across 47 overpermissioned roles, with generated least-privilege replacement policies awaiting deployment.

Evidence Readiness is 87%, up 4 points from last quarter. The cloud governance evidence fabric is operational across all 47 accounts with 98.7% event coverage.`;

const KPIS = [
  { key: 'debt',     icon: TrendingDown,  tone: 'success',  label: 'Governance debt',          value: '$847K',  delta: '−23% QoQ',           context: 'Within risk appetite ceiling of $1.2M', accent: '#10B981' },
  { key: 'material', icon: ShieldCheck,   tone: 'success',  label: 'Material events disclosed',value: '0 / 3',  delta: 'None crossed threshold', context: 'SEC 8-K Item 1.05 evaluation completed', accent: '#0D9488' },
  { key: 'premium',  icon: Briefcase,     tone: 'lens',     label: 'Cyber insurance premium',  value: '$1.4M',  delta: '−$200K vs prior',     context: 'Renewal Aug 17, 2026 · ' + INSURER, accent: '#0EA5E9' },
  { key: 'findings', icon: CheckCircle2,  tone: 'warning',  label: 'Audit findings remediated',value: '11 / 14',delta: '3 in flight',         context: AUDITOR + ' SOC 2 Type II', accent: '#F59E0B' },
];

const HEATMAP = [
  { id: 'INV-2026-042', label: 'PCI cardholder · cross-env trust', likelihood: 4, impact: 5, severity: 'critical' },
  { id: 'INV-2026-039', label: 'Stale credentials · 12 admins',    likelihood: 3, impact: 3, severity: 'high' },
  { id: 'INV-2026-031', label: 'Dev image in payments-prod',       likelihood: 4, impact: 3, severity: 'high' },
  { id: 'INV-2026-024', label: 'Config rule lag · stratum-banking',likelihood: 2, impact: 2, severity: 'medium' },
  { id: 'INV-2026-019', label: 'Suppressed false-positive backlog',likelihood: 1, impact: 1, severity: 'low' },
];

const RECOMMENDATIONS = [
  {
    id: 'rec-1',
    severity: 'awareness',
    title: 'Approve continued residual-risk acceptance for INV-2026-042',
    body: 'Active remediation in flight (3.5 hrs effort, projected −$263K debt). Materiality threshold not crossed. Recommendation: note for awareness; no board action required.',
  },
  {
    id: 'rec-2',
    severity: 'vote',
    title: 'Increase Cyber line budget +$220K for FY27',
    body: 'Driven by Macie + Inspector v2 expansion across 8 new acquired-entity accounts. Aligns with NYDFS 500.11 written program. Recommendation: vote at the September joint Risk + Audit committee meeting.',
  },
  {
    id: 'rec-3',
    severity: 'awareness',
    title: 'No Material Cybersecurity Incident disclosure required this quarter',
    body: 'Three events evaluated against the company\'s materiality framework (>$5M revenue impact OR PII >100K records OR regulatory MRA). None crossed threshold. Counsel concurred (memo on file).',
  },
];

const REC_STYLE = {
  vote:      { bg: 'bg-amber-50',    border: 'border-amber-300',    text: 'text-amber-700',     label: 'Board vote' },
  awareness: { bg: 'bg-slate-50',    border: 'border-slate-200',    text: 'text-slate-600',     label: 'For awareness' },
};

const FRAMEWORK_PALETTE = {
  'soc-2':   { stripe: '#0D9488', bg: 'bg-teal-50',   chip: 'text-apex-lens',    auditor: AUDITOR,        next: 'Mar 15, 2027', report: 'Type II — clean opinion' },
  'iso-27001': { stripe: '#0EA5E9', bg: 'bg-sky-50',   chip: 'text-sky-700',     auditor: 'BSI Americas', next: 'Sep 30, 2026', report: 'Surveillance — no NCs' },
  'pci-dss': { stripe: '#F59E0B', bg: 'bg-amber-50', chip: 'text-amber-700',     auditor: 'Coalfire QSA', next: 'Jun 12, 2026', report: 'ROC in draft' },
  'hipaa':   { stripe: '#8B5CF6', bg: 'bg-violet-50',chip: 'text-violet-700',    auditor: 'CliftonLarsonAllen', next: 'Nov 1, 2026', report: 'Risk analysis current' },
};

function frameworkSlug(name) { return name.toLowerCase().replace(/[^a-z]+/g, '-').replace(/-$/, ''); }

// ─── Page ───────────────────────────────────────────────────────────────────
export default function BoardReports() {
  const { seed, API } = useApp();
  const navigate = useNavigate();
  const [shareOpen, setShareOpen] = useState(false);
  const [activeExp, setActiveExp] = useState(null);
  const [activeFrm, setActiveFrm] = useState(null);
  const [activeRec, setActiveRec] = useState(null);

  const trendData = useMemo(() => {
    if (!seed) return [];
    return seed.governance_debt.trend_12m.map((m) => ({ ...m, value_k: m.value / 1000 }));
  }, [seed]);

  if (!seed) return null;
  const d = seed.governance_debt;
  const b = seed.board_reports;

  const exportBoardNote = () => {
    const url = `${API}/evidence-packets/INV-2026-042/pdf`;
    const a = document.createElement('a');
    a.href = url; a.download = 'apex-board-note-q2-2026.pdf';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    toast.success('Board note download started', { description: 'apex-board-note-q2-2026.pdf' });
  };

  const buildExposureRecord = (e, idx) => {
    const inv = ['INV-2026-042', 'INV-2026-039', 'INV-2026-031'][idx] ?? null;
    return {
      idTag: inv ?? `EXPOSURE-${idx + 1}`,
      title: e.label,
      severity: e.severity,
      subtitle: 'Active material exposure on the board pack.',
      meta: [
        { label: 'Investigation', value: inv ?? 'Not linked', mono: true, tone: inv ? 'critical' : '' },
        { label: 'Risk appetite breach', value: idx === 0 ? 'Yes — reportable' : 'No — within tolerance', tone: idx === 0 ? 'critical' : 'success' },
        { label: 'Owner', value: PREPARED_BY.name + ' (CISO)' },
        { label: 'Independent review', value: PEER_REVIEWER.name + ' (' + PEER_REVIEWER.role + ')' },
        { label: 'Quarterly debt impact', value: `~$${(80 + idx * 24)}K`, tone: 'critical', mono: true },
        { label: 'Insurance impact', value: idx === 0 ? '$50K deductible only' : 'No claim required' },
      ],
      sections: [
        {
          title: 'Why this is on the board pack',
          type: 'paragraph',
          body: 'Stratum\'s materiality framework requires escalation when an open finding has the potential to (a) impact >$5M of revenue, (b) involve >100,000 PII records, or (c) trigger a regulatory MRA. APEX correlates each finding against these thresholds in real time and surfaces only the items the board needs to see.',
        },
        {
          title: 'What the company has already done',
          type: 'list',
          items: [
            'Engaged Deloitte & Touche to validate the evidence chain (signed Apr 24)',
            'Notified the Risk Committee chair via secure channel within 4 business hours',
            'Pre-cleared the language with outside counsel (Wachtell Lipton)',
            'Documented the residual-risk acceptance memo · pinned in Audit Bundle BUN-2026-Q2-01',
          ],
        },
        {
          title: 'Action requested',
          type: 'paragraph',
          body: idx === 0
            ? 'Note this exposure for board awareness. APEX will auto-update if the materiality threshold changes — no board approval required at this stage.'
            : 'For awareness only. No board action required at this time.',
        },
      ],
      actions: [
        ...(inv ? [{ label: `Open ${inv}`, onClick: () => navigate(`/lens/investigations?id=${inv}`), primary: true }] : []),
        { label: 'Open Remediation', onClick: () => navigate('/lens/remediation') },
      ],
    };
  };

  const buildFrameworkRecord = (f) => {
    const slug = frameworkSlug(f.name);
    const palette = FRAMEWORK_PALETTE[slug] ?? FRAMEWORK_PALETTE['soc-2'];
    return {
      idTag: f.name,
      title: `${f.name} · ${f.coverage_pct}% coverage`,
      severity: f.gaps === 0 ? 'enforced' : f.gaps > 2 ? 'high' : 'medium',
      subtitle: `${palette.report} · auditor: ${palette.auditor}`,
      meta: [
        { label: 'Coverage', value: `${f.coverage_pct}%`, mono: true, tone: f.coverage_pct >= 90 ? 'success' : 'warning' },
        { label: 'Open gaps', value: f.gaps, mono: true, tone: f.gaps === 0 ? 'success' : 'critical' },
        { label: 'Auditor', value: palette.auditor },
        { label: 'Next audit', value: palette.next, mono: true },
        { label: 'Report status', value: palette.report, tone: palette.report.includes('clean') ? 'success' : '' },
        { label: 'Last refresh', value: '4 minutes ago', mono: true },
      ],
      sections: [
        {
          title: `${f.name} board narrative`,
          type: 'paragraph',
          body: 'APEX recomputes coverage every time new CloudTrail or Config evidence arrives. Coverage = share of in-scope controls with a current signed evidence packet within the framework\'s required cadence. Auditors get framework-mapped packets on demand.',
        },
        {
          title: 'Path to 100%',
          type: 'list',
          items: [
            'Pull packets for any open gap directly from Audit Workspace',
            'Pin recurring evidence so the next cycle is one click',
            'Cross-framework overlaps reduce automatically as linked investigations close',
          ],
        },
      ],
      actions: [
        { label: 'Open Audit Workspace', onClick: () => navigate('/lens/audit'), primary: true },
        { label: 'Open Evidence Vault', onClick: () => navigate('/lens/evidence') },
      ],
    };
  };

  const buildRecommendationRecord = (r) => ({
    idTag: r.id.toUpperCase(),
    title: r.title,
    severity: r.severity === 'vote' ? 'high' : 'medium',
    subtitle: REC_STYLE[r.severity]?.label,
    meta: [
      { label: 'Type', value: REC_STYLE[r.severity]?.label },
      { label: 'Prepared by', value: PREPARED_BY.name + ' (CISO)' },
      { label: 'Reviewed by', value: PEER_REVIEWER.name },
      { label: 'Date', value: AS_OF, mono: true },
    ],
    sections: [
      { title: 'Recommendation', type: 'paragraph', body: r.body },
      {
        title: 'Supporting documents available',
        type: 'list',
        items: [
          'Materiality assessment memo (signed by counsel)',
          'Independent reviewer sign-off (Internal Audit)',
          'Evidence packet: ' + (r.id === 'rec-1' ? 'EVD-2026-04-21-INV-2026-042' : 'EVD-2026-Q2-summary'),
          'Bundled in Audit Bundle BUN-2026-Q2-01',
        ],
      },
    ],
    actions: [
      { label: 'Open Evidence Vault', onClick: () => navigate('/lens/evidence'), primary: true },
      { label: 'Open Audit Workspace', onClick: () => navigate('/lens/audit') },
    ],
  });

  return (
    <div className="space-y-6 pb-12" data-testid="board-reports">

      {/* ─── Executive cover ─────────────────────────────────────────── */}
      <section className="relative bg-apex-shell text-white rounded-xl overflow-hidden grain border border-slate-700">
        <div className="absolute inset-0 bg-gradient-to-br from-apex-lens/10 via-transparent to-transparent" />
        <div className="relative p-6 sm:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-apex-lens font-semibold">
                <FileSignature className="w-3 h-3" />
                Confidential · Risk Committee Pack
              </div>
              <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Georgia, "Source Serif Pro", serif' }}>
                {QUARTER} Cybersecurity &amp; Governance Report
              </h1>
              <p className="text-sm text-slate-300 mt-2 max-w-2xl">
                Prepared for the Stratum Financial Group Board Risk Committee. Covers the period from Feb 1, 2026 through May 6, 2026.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-[11px] font-mono text-slate-400">
                <span>As of {AS_OF}</span><span>·</span>
                <span>Prepared by {PREPARED_BY.name}, CISO</span><span>·</span>
                <span>Independently reviewed by {PEER_REVIEWER.name}, Internal Audit</span>
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-3 py-1">
                <CheckCircle2 className="w-3 h-3" />Within risk appetite
              </span>
              <div className="flex flex-wrap gap-2 mt-1">
                <Button onClick={() => setShareOpen(true)} data-testid="board-share-button" variant="outline" className="bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 gap-1.5"><Share2 className="w-3.5 h-3.5" />Share</Button>
                <Button onClick={exportBoardNote} data-testid="board-export-button" className="bg-apex-lens hover:bg-apex-lens-dark text-white gap-1.5"><Download className="w-3.5 h-3.5" />Export Board Note</Button>
              </div>
            </div>
          </div>

          {/* Executive summary paragraph */}
          <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4 sm:p-5 mt-2">
            <h3 className="text-[10px] uppercase tracking-wider text-apex-lens font-bold mb-2">Executive summary</h3>
            <p className="text-sm leading-relaxed text-slate-200">
              {b.summary} Governance debt declined 23% quarter-over-quarter, driven by closure of three IAM cross-environment paths and remediation of one production image of dev origin. One critical investigation remains open <span className="inline-block align-middle"><InvestigationBadge id="INV-2026-042" /></span>; APEX has assembled the signed evidence packet, counsel has reviewed, and remediation is in flight (estimated 3.5 hours · projected −$263K debt). <strong className="text-white">No event evaluated this quarter crossed Stratum&apos;s materiality threshold for SEC 8-K Item 1.05 disclosure.</strong>
            </p>
          </div>
        </div>
      </section>

      {/* ─── Board narrative — copyable for board package ────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="board-narrative">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-apex-lens" />
            <h3 className="text-sm font-bold text-apex-ink">Board narrative — Q2 2026</h3>
          </div>
          <button
            onClick={() => {
              navigator.clipboard?.writeText(BOARD_NARRATIVE);
              toast.success('Narrative copied to clipboard');
            }}
            className="text-[11px] font-semibold text-apex-lens hover:text-teal-600 transition-colors inline-flex items-center gap-1"
            data-testid="copy-narrative"
          >
            <Copy className="w-3 h-3" />
            Copy
          </button>
        </div>
        <div
          className="text-[13px] text-apex-ink-2 leading-[1.8] bg-slate-50 rounded-lg border border-slate-200 p-4 whitespace-pre-line"
          style={{ fontFamily: 'Georgia, "Source Serif Pro", serif' }}
          data-testid="narrative-text"
        >
          {BOARD_NARRATIVE}
        </div>
      </div>

      {/* ─── Quarterly scorecard — Q1 vs Q2 at a glance ──────────────── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="board-scorecard">
        {[
          { label: 'Governance Debt',      q1: '$1.1M', q2: '$847K', delta: '↓ 23%' },
          { label: 'Evidence Readiness',   q1: '79%',   q2: '87%',   delta: '↑ 8pp' },
          { label: 'Control Gaps',         q1: '6',     q2: '3',     delta: '↓ 50%' },
          { label: 'Contamination Score',  q1: '33',    q2: '23',    delta: '↓ 30%' },
        ].map((item) => (
          <div key={item.label} className="bg-white rounded-xl border border-slate-200 p-4 text-center" data-testid={`scorecard-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="text-[10px] uppercase tracking-wider text-apex-neutral font-semibold mb-2">
              {item.label}
            </div>
            <div className="flex items-end justify-center gap-3">
              <div>
                <div className="text-[10px] font-mono text-slate-400 mb-0.5">Q1 2026</div>
                <div className="text-base font-semibold text-slate-400">{item.q1}</div>
              </div>
              <div className="text-apex-success font-mono text-[11px] mb-1">{item.delta}</div>
              <div>
                <div className="text-[10px] font-mono text-apex-lens mb-0.5">Q2 2026</div>
                <div className="text-base font-bold text-apex-ink">{item.q2}</div>
              </div>
            </div>
            <div className="mt-2 text-[11px] font-semibold text-apex-success">{item.delta}</div>
          </div>
        ))}
      </div>

      {/* ─── KPI strip — 4 cards, distinct accent borders ────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="board-kpis">
        {KPIS.map((k, i) => (
          <motion.div
            key={k.key}
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            data-testid={`board-kpi-${k.key}`}
            className="relative bg-white rounded-lg border border-slate-200 p-4 overflow-hidden"
          >
            <span className="absolute left-0 top-0 bottom-0 w-1" style={{ backgroundColor: k.accent }} />
            <div className="flex items-center gap-2 mb-1">
              <k.icon className="w-3.5 h-3.5" style={{ color: k.accent }} />
              <span className="text-[10px] uppercase tracking-wider font-semibold text-apex-neutral">{k.label}</span>
            </div>
            <div className="text-2xl font-bold tabular text-apex-ink">{k.value}</div>
            <div className="text-[11px] font-mono mt-0.5" style={{ color: k.accent }}>{k.delta}</div>
            <div className="text-[10px] text-apex-neutral mt-2 leading-relaxed">{k.context}</div>
          </motion.div>
        ))}
      </section>

      {/* ─── Trend chart with risk-appetite ceiling + materiality line ─ */}
      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
          <div>
            <h3 className="text-sm font-bold text-apex-ink flex items-center gap-2"><GanttChart className="w-3.5 h-3.5 text-apex-lens" />Governance debt — 12-month</h3>
            <p className="text-[11px] text-apex-neutral mt-0.5">Risk-appetite ceiling shown as dashed line. Materiality threshold marked.</p>
          </div>
          <div className="flex items-center gap-3 text-[10px] font-mono text-apex-neutral">
            <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-apex-lens" />Debt</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-px bg-apex-warning" style={{ borderTop: '1px dashed' }} />Appetite ceiling</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-px bg-apex-critical" />Materiality</span>
          </div>
        </div>
        <div className="h-64 w-full min-h-[256px]">
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1} debounce={50}>
            <AreaChart data={trendData}>
              <defs>
                <linearGradient id="brdebt" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0D9488" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#0D9488" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid stroke="#E2E8F0" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#64748B' }} stroke="#E2E8F0" />
              <YAxis tick={{ fontSize: 11, fill: '#64748B' }} stroke="#E2E8F0" tickFormatter={(v) => `$${v}K`} />
              <Tooltip contentStyle={{ backgroundColor: '#0F172A', borderRadius: 8, border: '1px solid #1E293B', color: 'white' }} formatter={(v) => [`$${Number(v).toLocaleString()}K`, 'Debt']} />
              <ReferenceLine y={1200} stroke="#D97706" strokeDasharray="6 4" label={{ value: 'Risk appetite ceiling · $1.2M', fill: '#D97706', fontSize: 10, position: 'insideTopRight' }} />
              <ReferenceLine y={5000} stroke="#DC2626" strokeWidth={1.2} label={{ value: 'SEC materiality · $5M', fill: '#DC2626', fontSize: 10, position: 'insideTopRight' }} />
              <Area type="monotone" dataKey="value_k" stroke="#0D9488" strokeWidth={2} fill="url(#brdebt)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* ─── Risk heatmap + Top exposures ───────────────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Heatmap */}
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-sm font-bold text-apex-ink">Risk heatmap · likelihood × impact</h3>
            <span className="text-[10px] font-mono text-apex-neutral">{HEATMAP.length} active items</span>
          </div>
          <Heatmap items={HEATMAP} onPick={(it) => navigate(`/lens/investigations?id=${it.id}`)} />
          <p className="text-[11px] text-apex-neutral mt-3 leading-relaxed">
            APEX plots every active investigation against Stratum&apos;s standard 5×5 risk matrix using the impact and likelihood values that drive the debt model. Items in the upper-right quadrant are subject to enhanced board reporting.
          </p>
        </div>

        {/* Top exposures */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-sm font-bold text-apex-ink mb-3">Top active exposures</h3>
          <div className="space-y-2">
            {b.exposures.map((e, i) => {
              const sevColor = e.severity === 'critical' ? '#DC2626' : e.severity === 'high' ? '#D97706' : '#0EA5E9';
              return (
                <motion.button
                  key={i}
                  type="button"
                  initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}
                  onClick={() => setActiveExp({ e, idx: i })}
                  data-testid={`board-exposure-${i}`}
                  className="w-full text-left flex items-start gap-2 p-2 rounded-md border border-slate-200 hover:border-apex-lens hover:bg-slate-50 transition-all group"
                  style={{ borderLeft: `3px solid ${sevColor}` }}
                >
                  <SeverityBadge severity={e.severity} size="sm" icon={false} />
                  <p className="flex-1 text-xs text-apex-ink-2 leading-snug">{e.label}</p>
                  <ArrowRight className="w-3 h-3 text-slate-300 group-hover:text-apex-lens transition-colors shrink-0 mt-0.5" />
                </motion.button>
              );
            })}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] font-mono text-apex-neutral">
            Each exposure links to a signed evidence packet in the Vault.
          </div>
        </div>
      </section>

      {/* ─── Materiality + Disclosure assessment ─────────────────── */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Scale className="w-4 h-4 text-apex-lens" />
            <h3 className="text-sm font-bold text-apex-ink">SEC Item 1.05 materiality assessment</h3>
          </div>
          <p className="text-[12px] text-apex-ink-2 leading-relaxed mb-3">
            Three events evaluated this quarter against Stratum&apos;s materiality framework. <strong>None crossed the threshold</strong> (any of: &gt;$5M revenue impact, &gt;100,000 PII records, regulatory MRA/MRIA, or qualitative reputational impact endorsed by Disclosure Committee).
          </p>
          <div className="rounded-md border border-slate-200 bg-white divide-y divide-slate-100 text-[11px] font-mono">
            <Row k="Events evaluated" v="3" />
            <Row k="Disclosed (8-K filed)" v="0" tone="success" />
            <Row k="Counsel concurred" v="Yes — Wachtell Lipton" tone="success" />
            <Row k="Disclosure Committee chair" v="Sara Aoki, General Counsel" />
            <Row k="Memos pinned to bundle" v="BUN-2026-Q2-MAT" />
          </div>
        </div>

        <div className="rounded-xl bg-slate-50 border border-slate-200 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Building2 className="w-4 h-4 text-apex-lens" />
            <h3 className="text-sm font-bold text-apex-ink">External posture</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[12px]">
            <div>
              <h4 className="text-[10px] uppercase tracking-wider font-semibold text-apex-neutral mb-1.5">Auditor</h4>
              <div className="font-semibold text-apex-ink">{AUDITOR}</div>
              <div className="text-apex-neutral text-[11px] mt-0.5 font-mono">SOC 2 Type II — clean opinion · Mar 31</div>
              <div className="text-apex-neutral text-[11px] font-mono">No exceptions · 14 controls scoped</div>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-wider font-semibold text-apex-neutral mb-1.5">Cyber insurance</h4>
              <div className="font-semibold text-apex-ink">{INSURER}</div>
              <div className="text-apex-neutral text-[11px] mt-0.5 font-mono">Policy renewal Aug 17, 2026</div>
              <div className="text-apex-neutral text-[11px] font-mono">Premium $1.4M · sublimit $25M</div>
            </div>
            <div className="sm:col-span-2 mt-1">
              <h4 className="text-[10px] uppercase tracking-wider font-semibold text-apex-neutral mb-1.5">Regulators</h4>
              <ul className="space-y-1 text-apex-ink-2 text-[11px]">
                {REGULATORS.map((r) => (
                  <li key={r} className="flex items-center gap-2">
                    <CheckCircle2 className="w-3 h-3 text-apex-success" />
                    {r}
                    <span className="ml-auto text-apex-neutral font-mono">no MRAs / MRIAs open</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Framework readiness — colored stripes per framework ───── */}
      <section>
        <h3 className="text-sm font-bold text-apex-ink mb-3">Framework readiness</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {seed.framework_coverage.map((f, i) => {
            const slug = frameworkSlug(f.name);
            const palette = FRAMEWORK_PALETTE[slug] ?? FRAMEWORK_PALETTE['soc-2'];
            return (
              <motion.button
                key={f.name}
                type="button"
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
                onClick={() => setActiveFrm(f)}
                data-testid={`board-framework-${slug}`}
                className="relative bg-white rounded-lg border border-slate-200 text-left hover:shadow-sm hover:-translate-y-0.5 transition-all overflow-hidden"
              >
                <span className="absolute top-0 inset-x-0 h-1" style={{ backgroundColor: palette.stripe }} />
                <div className="p-4 pt-5">
                  <div className={`text-[10px] uppercase tracking-wider font-bold ${palette.chip}`}>{f.name}</div>
                  <div className="text-2xl font-bold tabular text-apex-ink mt-1">{f.coverage_pct}%</div>
                  <div className="text-[11px] text-apex-neutral">{f.gaps} gap{f.gaps !== 1 ? 's' : ''}</div>
                  <div className="mt-2 pt-2 border-t border-slate-100 space-y-0.5 text-[10px] font-mono text-apex-neutral">
                    <div className="truncate">{palette.auditor}</div>
                    <div className="truncate">Next: {palette.next}</div>
                  </div>
                </div>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* ─── Board recommendations ───────────────────────────────── */}
      <section className="bg-amber-50/40 border border-amber-200 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <PenLine className="w-4 h-4 text-amber-700" />
          <h3 className="text-sm font-bold text-apex-ink">Board recommendations</h3>
        </div>
        <p className="text-[11px] text-apex-neutral mb-3">Items requiring board awareness or vote at the September joint Risk + Audit committee meeting.</p>
        <div className="space-y-2">
          {RECOMMENDATIONS.map((r, i) => {
            const s = REC_STYLE[r.severity];
            return (
              <motion.button
                key={r.id}
                type="button"
                initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.07 }}
                onClick={() => setActiveRec(r)}
                data-testid={`board-rec-${r.id}`}
                className={`w-full text-left rounded-lg border ${s.border} ${s.bg} p-3 hover:shadow-sm transition-all group`}
              >
                <div className="flex items-start gap-3">
                  <span className={`text-[9px] uppercase tracking-wider font-bold ${s.text} bg-white border ${s.border} rounded-full px-2 py-0.5 shrink-0 whitespace-nowrap`}>
                    {s.label}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-apex-ink leading-snug">{r.title}</h4>
                    <p className="text-[11px] text-apex-ink-2 mt-1 leading-relaxed">{r.body}</p>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-slate-400 group-hover:text-apex-lens transition-colors shrink-0 mt-1" />
                </div>
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* ─── CISO sign-off footer ───────────────────────────── */}
      <section className="bg-apex-shell text-white rounded-xl p-5 sm:p-6 grain border border-slate-700">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <h3 className="text-sm font-bold mb-2 flex items-center gap-2"><FileSignature className="w-3.5 h-3.5 text-apex-lens" />Attestation</h3>
            <p className="text-[12px] text-slate-300 leading-relaxed max-w-2xl">
              I, {PREPARED_BY.name}, in my capacity as Chief Information Security Officer of Stratum Financial Group, attest that the information presented in this report fairly reflects the cybersecurity and governance posture of the company as of {AS_OF}, based on the immutable evidence chain maintained by the APEX Lens platform. Independent review performed by {PEER_REVIEWER.name}, {PEER_REVIEWER.role}.
            </p>
          </div>
          <div className="space-y-3">
            <Signature name={PREPARED_BY.name} role={PREPARED_BY.role} />
            <Signature name={PEER_REVIEWER.name} role={PEER_REVIEWER.role + ' · independent reviewer'} muted />
          </div>
        </div>
        <div className="mt-5 pt-4 border-t border-slate-700 flex flex-wrap items-center justify-between gap-3 text-[10px] font-mono text-slate-400">
          <span>Document ID: APEX-BOARD-2026-Q2 · cryptographically sealed at export</span>
          <span>Powered by APEX Lens · evidence fabric notarised hourly</span>
        </div>
      </section>

      {/* Drawers + share */}
      <ShareSnapshotDialog open={shareOpen} onOpenChange={setShareOpen} />
      <RecordDrawer open={!!activeExp} onOpenChange={(o) => !o && setActiveExp(null)} record={activeExp ? buildExposureRecord(activeExp.e, activeExp.idx) : null} testId="board-exposure-drawer" />
      <RecordDrawer open={!!activeFrm} onOpenChange={(o) => !o && setActiveFrm(null)} record={activeFrm ? buildFrameworkRecord(activeFrm) : null} testId="board-framework-drawer" />
      <RecordDrawer open={!!activeRec} onOpenChange={(o) => !o && setActiveRec(null)} record={activeRec ? buildRecommendationRecord(activeRec) : null} testId="board-rec-drawer" />
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────
function Heatmap({ items, onPick }) {
  // 5×5 grid: rows = impact (5 top, 1 bottom), cols = likelihood (1 left, 5 right)
  const cells = Array.from({ length: 5 }, () => Array.from({ length: 5 }, () => []));
  items.forEach((it) => {
    const rowIdx = 5 - it.impact;
    const colIdx = it.likelihood - 1;
    cells[rowIdx][colIdx].push(it);
  });
  // Background gradient — green bottom-left → red top-right
  const cellColor = (r, c) => {
    const score = (4 - r) + c; // 0..8
    if (score >= 7) return 'bg-red-100';
    if (score >= 5) return 'bg-amber-100';
    if (score >= 3) return 'bg-yellow-50';
    return 'bg-emerald-50';
  };
  return (
    <div className="grid grid-cols-[24px,1fr] gap-2">
      <div className="flex items-center justify-center text-[9px] uppercase tracking-wider font-mono text-apex-neutral [writing-mode:vertical-rl] rotate-180">Impact</div>
      <div>
        <div className="grid grid-cols-5 gap-1">
          {cells.map((row, r) =>
            row.map((items, c) => (
              <div
                key={`${r}-${c}`}
                className={`relative aspect-[3/2] rounded-md border border-slate-200 ${cellColor(r, c)} flex flex-wrap items-start gap-1 p-1.5`}
              >
                {items.map((it) => {
                  const sevColor = it.severity === 'critical' ? '#DC2626' : it.severity === 'high' ? '#D97706' : it.severity === 'medium' ? '#0EA5E9' : '#10B981';
                  return (
                    <button
                      key={it.id}
                      type="button"
                      onClick={() => onPick(it)}
                      data-testid={`heatmap-${it.id}`}
                      className="text-[9px] font-mono font-semibold text-white rounded px-1 py-0.5 leading-none hover:scale-105 transition-transform shadow-sm"
                      style={{ backgroundColor: sevColor }}
                      title={it.label}
                    >
                      {it.id.replace('INV-2026-', '')}
                    </button>
                  );
                })}
              </div>
            )),
          )}
        </div>
        <div className="grid grid-cols-5 mt-1 text-[9px] uppercase tracking-wider font-mono text-apex-neutral text-center">
          {['Rare', 'Unlikely', 'Possible', 'Likely', 'Certain'].map((l) => <span key={l}>{l}</span>)}
        </div>
        <div className="mt-1 text-[9px] uppercase tracking-wider font-mono text-apex-neutral text-center">Likelihood →</div>
      </div>
    </div>
  );
}

function Row({ k, v, tone }) {
  return (
    <div className="flex items-baseline justify-between gap-3 px-3 py-1.5">
      <span className="text-apex-neutral">{k}</span>
      <span className={`${tone === 'success' ? 'text-apex-success' : tone === 'critical' ? 'text-apex-critical' : 'text-apex-ink-2'}`}>{v}</span>
    </div>
  );
}

function Signature({ name, role, muted = false }) {
  return (
    <div className={muted ? 'opacity-80' : ''}>
      <div className="font-mono text-apex-lens text-2xl leading-none" style={{ fontFamily: '"Caveat", "Brush Script MT", cursive' }}>
        {name}
      </div>
      <div className="border-b border-slate-700 mt-1 mb-1" />
      <div className="text-[11px] font-semibold text-white">{name}</div>
      <div className="text-[10px] text-slate-400">{role}</div>
    </div>
  );
}
